<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h2 class="text-2xl font-bold text-gray-800 flex items-center gap-2">
        User-Credential Assignment
        <BaseInfoIcon :text="ERKLAERUNG.benutzerZuordnung" label="User-Credential Assignment" />
      </h2>
      <div class="flex items-center space-x-4">
        <BaseButton
          class="min-w-[170px] whitespace-nowrap"
          @click="openCreateDialog"
        >
          Create Local User
        </BaseButton>
        <BaseInfoIcon :text="ERKLAERUNG.lokalenBenutzerAnlegen" label="Create Local User" />
        <BaseSelect
          v-model="selectedUserType"
          :options="userTypeOptions"
          :showPlaceholder="true"
          placeholder="Filter by User Type"
          @update:modelValue="loadUsers"
        />
        <BaseSelect
          v-model="selectedCredentialFilter"
          :options="credentialFilterOptions"
          :showPlaceholder="true"
          placeholder="Filter by Credential"
          @update:modelValue="loadUsers"
        />
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
      {{ errorMessage }}
    </div>

    <!-- Success Message -->
    <div v-if="successMessage" class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
      {{ successMessage }}
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <p class="text-gray-600">Loading users...</p>
    </div>

    <!-- Users Table -->
    <div v-else class="bg-white rounded-lg shadow overflow-x-auto">
      <table class="w-full min-w-[960px] table-fixed">
        <colgroup>
          <col class="w-16" />
          <col class="w-48" />
          <col class="w-48" />
          <col class="w-48" />
          <col />
          <col class="w-44" />
        </colgroup>
        <thead class="bg-gray-100">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              ID
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Name
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Username
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              User Type
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Assigned Credentials
            </th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr
            v-for="user in sortedUsers"
            :key="user.id"
            :class="user.user_type === 'unassigned' || needsDiscordUsernameFor(user)
              ? 'bg-yellow-50 hover:bg-yellow-100'
              : 'hover:bg-gray-50'"
          >
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {{ user.id }}
            </td>
            <td class="px-6 py-4">
              <div class="flex flex-col items-start gap-1">
                <span class="break-words text-sm font-medium text-gray-900">
                  {{ user.first_name }} {{ user.last_name }}
                </span>
                <span
                  v-if="user.user_type === 'unassigned'"
                  class="inline-flex items-center rounded-full bg-yellow-200 text-yellow-800 px-2 py-0.5 text-xs font-semibold"
                >
                  Needs assignment
                </span>
                <span
                  v-else-if="needsDiscordUsernameFor(user)"
                  class="inline-flex shrink-0 items-center rounded-full bg-yellow-200 px-2 py-0.5 text-xs font-semibold text-yellow-800"
                >
                  Discord name missing
                </span>
              </div>
            </td>
            <td class="px-6 py-4 text-sm text-gray-600">
              <span class="block truncate" :title="user.username">{{ user.username }}</span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="flex items-center gap-2">
                <span
                  :class="[
                    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                    user.user_type === 'professor' ? 'bg-purple-100 text-purple-700' :
                    user.user_type === 'student' ? 'bg-blue-100 text-blue-700' :
                    user.user_type === 'admin' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  ]"
                >
                  {{ user.user_type }}
                </span>
                <span
                  class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                  :class="user.auth_provider?.toLowerCase() === 'keycloak'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-700'"
                >
                  {{ user.auth_provider?.toLowerCase() === 'keycloak' ? 'HTWG (OIDC)' : 'Local' }}
                </span>
              </div>
            </td>
            <td class="px-6 py-4">
              <div v-if="userCredentials[user.id]" class="flex flex-wrap gap-1">
                <span
                  v-for="cred in userCredentials[user.id]"
                  :key="cred.id"
                  :class="[
                    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                    cred.is_primary ? 'bg-green-100 text-green-800 ring-2 ring-green-400' : 'bg-gray-100 text-gray-700'
                  ]"
                  :title="cred.is_primary ? 'Primary credential' : ''"
                >
                  {{ cred.name }}
                  <span v-if="cred.is_primary" class="ml-1">★</span>
                </span>
              </div>
              <span v-else class="text-sm text-gray-400">Loading...</span>
            </td>
            <td class="whitespace-nowrap px-3 py-4 text-right text-sm font-medium">
              <div class="flex flex-nowrap justify-end gap-2">
                <BaseButton variant="edit" class="shrink-0 whitespace-nowrap px-3 py-1" @click="openAssignDialog(user)">
                  Manage
                </BaseButton>
                <BaseButton variant="delete" class="shrink-0 whitespace-nowrap px-3 py-1" @click="confirmDelete(user)">
                  Delete
                </BaseButton>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="sortedUsers.length === 0" class="text-center py-8 text-gray-500">
        No users found with the selected filters.
      </div>
    </div>

    <!-- Quick Professor Assignment Section -->
    <div class="bg-blue-50 rounded-lg p-6 border border-blue-200">
      <h3 class="text-lg font-semibold text-gray-800 mb-4">
        Quick Professor Assignment
      </h3>
      <p class="text-sm text-gray-600 mb-4">
        Quickly view and manage which professors are assigned to which micro-credentials.
      </p>
      <BaseButton @click="showProfessorsOnly">
        View All Professors
      </BaseButton>
    </div>

    <!-- Create Local User Dialog -->
    <BaseDialog v-if="showCreateDialog" @close="closeCreateDialog">
      <template #title>
        Create Local User
      </template>
      <template #content>
        <div class="space-y-4">
          <div class="rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700">
            This account will use local email and password authentication.
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <BaseInput v-model="createForm.firstName" required />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
              <BaseInput v-model="createForm.lastName" required />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <BaseInput v-model="createForm.email" type="email" required />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Discord username *</label>
              <BaseInput v-model="createForm.username" required />
              <p class="mt-1 text-xs text-gray-500">
                Enter the user's exact Discord name. It is used to match incoming Discord messages.
              </p>
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Password *</label>
            <BaseInput v-model="createForm.password" type="password" required />
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">User Type *</label>
              <BaseSelect
                v-model="createForm.userType"
                :options="createUserTypeOptions"
                :showPlaceholder="false"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Micro-Credential *</label>
              <BaseSelect
                v-model="createForm.microCredentialId"
                :options="credentialOptions"
                :showPlaceholder="true"
                placeholder="Select credential"
              />
            </div>
          </div>
        </div>
      </template>
      <template #actions>
        <BaseButton variant="cancel" @click="closeCreateDialog">
          Cancel
        </BaseButton>
        <BaseButton
          @click="createUser"
          :disabled="creating || !isCreateFormValid"
        >
          {{ creating ? 'Creating...' : 'Create User' }}
        </BaseButton>
      </template>
    </BaseDialog>

    <!-- Assignment Dialog -->
    <BaseDialog v-if="showAssignDialog" @close="closeAssignDialog">
      <template #title>
        Manage User Settings
      </template>
      <template #content>
        <div class="space-y-4">
          <div>
            <p class="text-sm text-gray-600 mb-2">
              <strong>User:</strong> {{ selectedUser?.first_name }} {{ selectedUser?.last_name }}
              ({{ selectedUser?.email }})
            </p>
            <span
              class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
              :class="isOIDCAccount ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'"
            >
              {{ isOIDCAccount ? 'HTWG account' : 'Local account' }}
            </span>
            <p
              v-if="needsDiscordUsername"
              class="mt-2 rounded-md bg-yellow-50 px-3 py-2 text-xs text-yellow-800"
            >
              A Discord username must be entered before this user's Discord messages can be matched.
            </p>
          </div>

          <div
            v-if="assignmentError"
            class="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
            role="alert"
          >
            {{ assignmentError }}
          </div>

          <!-- User Details -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <BaseInput v-model="assignmentForm.firstName" :readonly="isOIDCAccount" :class="{ 'bg-gray-100': isOIDCAccount }" required />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
              <BaseInput v-model="assignmentForm.lastName" :readonly="isOIDCAccount" :class="{ 'bg-gray-100': isOIDCAccount }" required />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <BaseInput
                v-model="assignmentForm.email"
                type="email"
                :readonly="isOIDCAccount"
                :class="[
                  isOIDCAccount ? 'bg-gray-100' : '',
                  assignmentFieldErrors.email ? 'border-red-500 focus:ring-red-500' : ''
                ]"
                :aria-invalid="Boolean(assignmentFieldErrors.email)"
                :aria-describedby="assignmentFieldErrors.email ? 'assignment-email-error' : undefined"
                required
                @update:modelValue="clearAssignmentFieldError('email')"
              />
              <p
                v-if="assignmentFieldErrors.email"
                id="assignment-email-error"
                class="mt-1 text-xs text-red-700"
              >
                {{ assignmentFieldErrors.email }}
              </p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Discord username *</label>
              <BaseInput
                v-model="assignmentForm.username"
                :class="assignmentFieldErrors.username ? 'border-red-500 focus:ring-red-500' : ''"
                :aria-invalid="Boolean(assignmentFieldErrors.username)"
                :aria-describedby="assignmentFieldErrors.username ? 'assignment-username-error' : undefined"
                required
                @update:modelValue="clearAssignmentFieldError('username')"
              />
              <p
                v-if="assignmentFieldErrors.username"
                id="assignment-username-error"
                class="mt-1 text-xs text-red-700"
              >
                {{ assignmentFieldErrors.username }}
              </p>
              <p class="mt-1 text-xs text-gray-500">
                Enter the exact Discord name used by the student. This value is used for Discord message matching.
              </p>
            </div>
          </div>
          <div v-if="canEditPassword">
            <label class="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <BaseInput v-model="assignmentForm.password" type="password" placeholder="Leave blank to keep current password" />
          </div>

          <!-- User Type Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              User Type *
            </label>
            <BaseSelect
              v-model="assignmentForm.selectedUserType"
              :options="userTypeEditOptions"
              :showPlaceholder="false"
            />
            <p class="text-xs text-gray-500 mt-1">
              Change the user's role (student or professor)
            </p>
          </div>
          
          <!-- Multi-Select for Credentials -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Assigned Micro-Credentials *
            </label>
            <div class="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-2">
              <label
                v-for="credential in assignableCredentials"
                :key="credential.id"
                class="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
              >
                <input
                  type="checkbox"
                  :value="credential.id"
                  v-model="assignmentForm.selectedCredentialIds"
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span class="ml-2 text-sm text-gray-700">{{ credential.name }}</span>
              </label>
            </div>
            <p class="text-xs text-gray-500 mt-1">
              Select one or more credentials to assign to this user
            </p>
          </div>

          <!-- Primary Credential Selection -->
          <div v-if="assignmentForm.selectedCredentialIds.length > 0">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Primary Credential (Default on Login) *
            </label>
            <BaseSelect
              v-model="assignmentForm.primaryCredentialId"
              :options="primaryCredentialOptions"
              :showPlaceholder="true"
              placeholder="Select primary credential"
            />
            <p class="text-xs text-gray-500 mt-1">
              The primary credential will be used by default when the user logs in
            </p>
          </div>
        </div>
      </template>
      <template #actions>
        <BaseButton variant="cancel" @click="closeAssignDialog">
          Cancel
        </BaseButton>
        <BaseButton
          @click="assignUser"
          :disabled="assigning || !assignmentForm.selectedUserType || (assignmentForm.selectedUserType !== 'admin' && (assignmentForm.selectedCredentialIds.length === 0 || !assignmentForm.primaryCredentialId))"
        >
          {{ assigning ? 'Saving...' : 'Save Changes' }}
        </BaseButton>
      </template>
    </BaseDialog>

    <ConfirmModal
      :isOpen="showDeleteModal"
      title="Delete User"
      :message="`Are you sure you want to delete ${userToDelete?.first_name || 'this user'}?`"
      @confirm="deleteConfirmed"
      @cancel="showDeleteModal = false"
    />
  </div>
</template>

<script>
import {
  getAllUsers,
  createAdminUser,
  assignMultipleCredentialsToUser,
  getUserCredentials,
  updateUserType,
  updateAdminUser,
  deleteAdminUser,
} from '@/api/admin';
import { getAllMicroCredentials } from '@/api/admin';
import BaseButton from '@/BaseComponents/BaseButton.vue';
import BaseInfoIcon from '@/BaseComponents/BaseInfoIcon.vue';
import BaseInput from '@/BaseComponents/BaseInput.vue';
import BaseSelect from '@/BaseComponents/BaseSelect.vue';
import BaseDialog from '@/BaseComponents/BaseDialog.vue';
import ConfirmModal from '@/BaseComponents/ConfirmModal.vue';
import { ERKLAERUNG } from '@/async-sessions/erklaerungen';

export default {
  name: 'UserCredentialAssignment',
  components: {
    BaseButton,
    BaseInfoIcon,
    BaseInput,
    BaseSelect,
    BaseDialog,
    ConfirmModal,
  },
  data() {
    return {
      ERKLAERUNG,
      users: [],
      credentials: [],
      userCredentials: {},
      loading: false,
      assigning: false,
      errorMessage: '',
      assignmentError: '',
      assignmentFieldErrors: {
        email: '',
        username: '',
      },
      successMessage: '',
      showAssignDialog: false,
      showCreateDialog: false,
      showDeleteModal: false,
      creating: false,
      selectedUser: null,
      userToDelete: null,
      selectedUserType: '',
      selectedCredentialFilter: '',
      createForm: {
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        password: '',
        userType: 'student',
        microCredentialId: '',
      },
      assignmentForm: {
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        password: '',
        selectedCredentialIds: [],
        primaryCredentialId: '',
        selectedUserType: '',
      },
      userTypeOptions: [
        { value: '', label: 'All User Types' },
        { value: 'unassigned', label: 'Unassigned' },
        { value: 'professor', label: 'Professors' },
        { value: 'student', label: 'Students' },
        { value: 'admin', label: 'Admins' },
      ],
      userTypeEditOptions: [
        { value: 'student', label: 'Student' },
        { value: 'professor', label: 'Professor' },
        { value: 'admin', label: 'Admin' },
      ],
    };
  },
  computed: {
    createUserTypeOptions() {
      return [
        { value: 'student', label: 'Student' },
        { value: 'professor', label: 'Professor' },
      ];
    },
    isCreateFormValid() {
      const form = this.createForm;
      return form.firstName && form.lastName && form.email && form.username
        && form.password && form.userType && form.microCredentialId;
    },
    isOIDCAccount() {
      return this.selectedUser?.auth_provider?.toLowerCase() === 'keycloak';
    },
    canEditPassword() {
      return !this.isOIDCAccount;
    },
    needsDiscordUsername() {
      return this.selectedUser?.username?.startsWith('__oidc__') || false;
    },
    assignableCredentials() {
      // Filter out admin credential from assignment options
      return this.credentials.filter(
        (c) => c.name.toLowerCase() !== 'admin'
      );
    },
    credentialOptions() {
      return this.assignableCredentials.map((c) => ({
        value: String(c.id),
        label: c.name,
      }));
    },
    credentialFilterOptions() {
      // Filter out admin credential from filter options too
      const assignable = this.credentials.filter(
        (c) => c.name.toLowerCase() !== 'admin'
      );
      return [
        { value: '', label: 'All Credentials' },
        ...assignable.map((c) => ({
          value: String(c.id),
          label: c.name,
        })),
      ];
    },
    primaryCredentialOptions() {
      return this.assignableCredentials
        .filter((c) => this.assignmentForm.selectedCredentialIds.includes(c.id))
        .map((c) => ({
          value: String(c.id),
          label: c.name,
        }));
    },
    sortedUsers() {
      // Unassigned users need admin attention first, so list them above everyone else.
      return [...this.users].sort((a, b) => {
        const aUnassigned = a.user_type === 'unassigned' ? 0 : 1;
        const bUnassigned = b.user_type === 'unassigned' ? 0 : 1;
        return aUnassigned - bUnassigned;
      });
    },
  },
  watch: {
    // Auto-select primary credential if only one is selected
    'assignmentForm.selectedCredentialIds': function (newVal) {
      if (newVal.length === 1) {
        this.assignmentForm.primaryCredentialId = String(newVal[0]);
      } else if (newVal.length === 0) {
        this.assignmentForm.primaryCredentialId = '';
      } else if (!newVal.includes(parseInt(this.assignmentForm.primaryCredentialId))) {
        // Reset if primary is no longer in selected list
        this.assignmentForm.primaryCredentialId = '';
      }
    },
  },
  async mounted() {
    await this.loadCredentials();
    await this.loadUsers();
  },
  methods: {
    needsDiscordUsernameFor(user) {
      return user.username?.startsWith('__oidc__') || false;
    },
    openCreateDialog() {
      this.showCreateDialog = true;
    },
    closeCreateDialog() {
      this.showCreateDialog = false;
      this.createForm = {
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        password: '',
        userType: 'student',
        microCredentialId: '',
      };
    },
    async createUser() {
      this.creating = true;
      this.errorMessage = '';
      this.successMessage = '';
      try {
        await createAdminUser({
          first_name: this.createForm.firstName,
          last_name: this.createForm.lastName,
          email: this.createForm.email,
          username: this.createForm.username,
          password: this.createForm.password,
          user_type: this.createForm.userType,
          micro_credential_id: Number(this.createForm.microCredentialId),
        });
        this.closeCreateDialog();
        await this.loadUsers();
        this.successMessage = 'Local user created successfully';
      } catch (error) {
        this.errorMessage = error.response?.data?.detail || 'Failed to create user';
      } finally {
        this.creating = false;
      }
    },
    async loadCredentials() {
      try {
        const response = await getAllMicroCredentials();
        this.credentials = response.data;
      } catch (error) {
        this.errorMessage = error.response?.data?.detail || 'Failed to load credentials';
      }
    },
    async loadUsers() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const params = {};
        if (this.selectedUserType) {
          params.user_type = this.selectedUserType;
        }
        if (this.selectedCredentialFilter) {
          params.micro_credential_id = this.selectedCredentialFilter;
        }
        const response = await getAllUsers(params);
        this.users = response.data;
        
        // Load credentials for each user
        await this.loadAllUserCredentials();
      } catch (error) {
        this.errorMessage = error.response?.data?.detail || 'Failed to load users';
      } finally {
        this.loading = false;
      }
    },
    async loadAllUserCredentials() {
      // Load credentials for all users in parallel
      const promises = this.users.map(async (user) => {
        try {
          const response = await getUserCredentials(user.id);
          this.userCredentials[user.id] = response.data;
        } catch (error) {
          this.userCredentials[user.id] = [];
        }
      });
      await Promise.all(promises);
      // Force reactivity update
      this.userCredentials = { ...this.userCredentials };
    },
    async showProfessorsOnly() {
      this.selectedUserType = 'professor';
      this.selectedCredentialFilter = '';
      await this.loadUsers();
    },
    async openAssignDialog(user) {
      this.assignmentError = '';
      this.clearAssignmentFieldErrors();
      this.selectedUser = user;
      this.assignmentForm.firstName = user.first_name;
      this.assignmentForm.lastName = user.last_name;
      this.assignmentForm.email = user.email;
      this.assignmentForm.username = user.username;
      this.assignmentForm.password = '';
      
      // Set current user type
      this.assignmentForm.selectedUserType = user.user_type;
      
      // Load current credentials for this user
      try {
        const response = await getUserCredentials(user.id);
        const currentCredentials = response.data;
        
        this.assignmentForm.selectedCredentialIds = currentCredentials.map((c) => c.id);
        const primaryCred = currentCredentials.find((c) => c.is_primary);
        this.assignmentForm.primaryCredentialId = primaryCred ? String(primaryCred.id) : '';
      } catch (error) {
        this.assignmentForm.selectedCredentialIds = [];
        this.assignmentForm.primaryCredentialId = '';
      }
      
      this.showAssignDialog = true;
    },
    closeAssignDialog() {
      this.showAssignDialog = false;
      this.selectedUser = null;
      this.assignmentForm.selectedCredentialIds = [];
      this.assignmentForm.primaryCredentialId = '';
      this.assignmentForm.selectedUserType = '';
      this.assignmentForm.firstName = '';
      this.assignmentForm.lastName = '';
      this.assignmentForm.email = '';
      this.assignmentForm.username = '';
      this.assignmentForm.password = '';
      this.assignmentError = '';
      this.clearAssignmentFieldErrors();
    },
    async assignUser() {
      this.assigning = true;
      this.errorMessage = '';
      this.assignmentError = '';
      this.clearAssignmentFieldErrors();
      this.successMessage = '';
      try {
        const userData = {
          first_name: this.assignmentForm.firstName,
          last_name: this.assignmentForm.lastName,
          email: this.assignmentForm.email,
          username: this.assignmentForm.username,
        };
        if (this.assignmentForm.password) {
          userData.password = this.assignmentForm.password;
        }
        await updateAdminUser(this.selectedUser.id, userData);

        // Update user type if changed
        if (this.assignmentForm.selectedUserType !== this.selectedUser.user_type) {
          await updateUserType(
            this.selectedUser.id,
            this.assignmentForm.selectedUserType
          );
        }
        
        // Admin accounts do not use micro-credential assignments.
        if (this.assignmentForm.selectedUserType !== 'admin') {
          await assignMultipleCredentialsToUser(
            this.selectedUser.id,
            this.assignmentForm.selectedCredentialIds,
            parseInt(this.assignmentForm.primaryCredentialId)
          );
        }
        
        this.successMessage = `Successfully updated settings for ${this.selectedUser.first_name} ${this.selectedUser.last_name}`;
        this.closeAssignDialog();
        await this.loadUsers();
        setTimeout(() => {
          this.successMessage = '';
        }, 5000);
      } catch (error) {
        const detail = error.response?.data?.detail;
        let unassignedError = typeof detail === 'string'
          ? detail
          : detail
            ? JSON.stringify(detail)
            : error.message || 'Failed to update user settings';

        if (Array.isArray(detail)) {
          detail.forEach((validationError) => {
            const field = validationError.loc?.[validationError.loc.length - 1];
            if (field === 'email' || field === 'username') {
              this.assignmentFieldErrors[field] = validationError.msg;
            }
          });
          if (Object.values(this.assignmentFieldErrors).some(Boolean)) {
            unassignedError = '';
          }
        } else if (typeof detail === 'string') {
          const normalizedDetail = detail.toLowerCase();
          if (normalizedDetail.includes('username')) {
            this.assignmentFieldErrors.username = detail;
            unassignedError = '';
          } else if (normalizedDetail.includes('email')) {
            this.assignmentFieldErrors.email = detail;
            unassignedError = '';
          }
        }

        this.assignmentError = unassignedError;
      } finally {
        this.assigning = false;
      }
    },
    clearAssignmentFieldError(field) {
      if (Object.prototype.hasOwnProperty.call(this.assignmentFieldErrors, field)) {
        this.assignmentFieldErrors[field] = '';
      }
    },
    clearAssignmentFieldErrors() {
      this.assignmentFieldErrors.email = '';
      this.assignmentFieldErrors.username = '';
    },
    confirmDelete(user) {
      this.userToDelete = user;
      this.showDeleteModal = true;
    },
    async deleteConfirmed() {
      this.assigning = true;
      this.errorMessage = '';
      try {
        await deleteAdminUser(this.userToDelete.id);
        this.showDeleteModal = false;
        this.userToDelete = null;
        await this.loadUsers();
      } catch (error) {
        this.errorMessage = error.response?.data?.detail || 'Failed to delete user';
      } finally {
        this.assigning = false;
      }
    },
    getCredentialName(credentialId) {
      const credential = this.credentials.find((c) => c.id === credentialId);
      return credential ? credential.name : `ID: ${credentialId}`;
    },
  },
};
</script>
